// src/components/MarkdownModal.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent, // <-- 여기를 수정합니다.
  Box,
  CircularProgress
} from '@mui/material';
import MarkdownViewer from './MarkdownViewer';
import { getMarkdownContent, MARKDOWN_URLS } from '../../services/markdownService';

interface MarkdownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  markdownKey: keyof typeof MARKDOWN_URLS;
}

const MarkdownModal: React.FC<MarkdownModalProps> = ({
  open,
  onClose,
  title,
  markdownKey
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      if (!open) return;

      try {
        setLoading(true);
        setError(null);
        const markdownContent = await getMarkdownContent(markdownKey);
        setContent(markdownContent);
      } catch (error) {
        console.error('마크다운 콘텐츠 로드 실패:', error);
        setError('콘텐츠를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [open, markdownKey]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        {title}
        {/* <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton> */}
      </DialogTitle>

      <DialogContent sx={{
        px: 3,
        pb: 3,
        overflowY: 'auto', // 세로 스크롤 허용
        // 아래 CSS 속성을 추가하여 내용이 넘칠 때 자동으로 줄바꿈하고 크기를 조절합니다.
        '& img': { // 이미지 너비를 부모 요소에 맞춤
          maxWidth: '100%',
          height: 'auto',
          display: 'block' // 불필요한 하단 여백 제거
        },
        '& table': { // 테이블 너비를 부모 요소에 맞춤
          maxWidth: '100%',
          display: 'block', // 스크롤바를 위해 필요
          overflowX: 'auto', // 테이블이 길 경우 가로 스크롤 허용
          whiteSpace: 'nowrap', // 텍스트 줄바꿈 방지 (테이블 셀 내용에 한해)
        },
        '& code': { // 인라인 코드 또는 코드 블록
          wordBreak: 'break-word', // 긴 단어를 강제로 줄바꿈
          whiteSpace: 'pre-wrap',  // 공백 유지하면서 줄바꿈
        },
        '& pre': { // 코드 블록
          wordBreak: 'break-word', // 긴 단어를 강제로 줄바꿈
          whiteSpace: 'pre-wrap',  // 공백 유지하면서 줄바꿈
          overflowX: 'auto',      // 코드 블록이 길 경우 가로 스크롤 허용
        },
        wordBreak: 'keep-all', // 기본 줄바꿈 동작 설정 (한글 등)
        overflowWrap: 'break-word', // 긴 단어 강제 줄바꿈
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ color: 'error.main', p: 2 }}>{error}</Box>
        ) : (
          <MarkdownViewer content={content} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MarkdownModal;
